/**
 * Routes de run (DECISIONS §4.2, `docs/api-v1.md` §8.4).
 *
 * Depuis M2, **toutes** les mutations du parcours passent par le serveur : lancement de vague
 * (composition officielle), pose de tour, amélioration de tour, clôture de vague, tirage et choix
 * de carte, fin de run. Le client ne fait plus aucune projection locale (DECISIONS §1.3 A-1).
 *
 * **Anti-triche (DECISIONS §8)** : aucun de ces corps ne contient de montant, de niveau ni de
 * total. La pose envoie `{towerId, emplacementId, jokerConsomme}` ; la clôture envoie
 * `{ennemisTues[], fuites[], pvParcoursRestant}` — des **faits**. Les éclats, le niveau d'une
 * tour et le déroulement sont recalculés par le serveur.
 */
import {
  cardChooseResponseSchema,
  cardOptionsResponseSchema,
  runCreateResponseSchema,
  runEndResponseSchema,
  runResponseSchema,
  towerPlaceResponseSchema,
  towerUpgradeResponseSchema,
  waveResultResponseSchema,
  waveStartResponseSchema,
  type CardChooseInput,
  type CardChooseResponse,
  type CardOptionsInput,
  type CardOptionsResponse,
  type RunCreateInput,
  type RunCreateResponse,
  type RunDetail,
  type RunEndInput,
  type RunEndResponse,
  type TowerPlaceInput,
  type TowerPlaceResponse,
  type TowerUpgradeResponse,
  type WaveResultInput,
  type WaveResultResponse,
  type WaveStartResponse,
} from '@elemora/shared/api';
import { API_PATHS } from '@elemora/shared/constants';

import { genererCleIdempotence, requete } from './http';

/** `POST /runs` : le serveur génère la graine et l’état initial (éclats, jokers, PV). */
export async function creerRun(entree: RunCreateInput): Promise<RunCreateResponse> {
  return requete({
    methode: 'POST',
    chemin: API_PATHS.runs.create,
    schema: runCreateResponseSchema,
    corps: entree,
  });
}

/** `GET /runs/:runId` : état d’autorité d’une run (source de **toutes** les valeurs affichées). */
export async function lireRun(runId: string): Promise<RunDetail> {
  const reponse = await requete({
    methode: 'GET',
    chemin: API_PATHS.runs.byId(runId),
    schema: runResponseSchema,
  });
  return reponse.run;
}

/**
 * `POST /runs/:runId/waves/:index/start` — composition **officielle** de la vague.
 *
 * Le corps est vide : le serveur décide quels ennemis apparaissent, quand, et sous quel
 * `spawnId`. Le client simule à partir de cette composition, jamais à partir du contenu local :
 * sans cela, les faits envoyés à la clôture porteraient des `spawnId` inconnus du serveur
 * (`422 RUN_INCOHERENT`, DECISIONS §8.3b).
 *
 * Rejouable : une vague déjà `en_cours` renvoie la même composition.
 */
export async function demarrerVagueRun(runId: string, index: number): Promise<WaveStartResponse> {
  return requete({
    methode: 'POST',
    chemin: API_PATHS.runs.waveStart(runId, index),
    schema: waveStartResponseSchema,
    corps: {},
  });
}

/**
 * `POST /runs/:runId/towers` — pose d’une tour, débitée **côté serveur**.
 *
 * `jokerConsomme` est un fait de jeu : `true` pendant une vague (prix de la tour **et** 1 joker,
 * DECISIONS §1.3 A-18), `false` entre deux vagues. Un refus ne consomme rien.
 */
export async function placerTourRun(
  runId: string,
  entree: TowerPlaceInput,
): Promise<TowerPlaceResponse> {
  return requete({
    methode: 'POST',
    chemin: API_PATHS.runs.towerPlace(runId),
    schema: towerPlaceResponseSchema,
    corps: entree,
    cleIdempotence: genererCleIdempotence(),
  });
}

/**
 * `POST /runs/:runId/waves/:index/result` — clôture d’une vague (idempotente).
 *
 * `Idempotency-Key` obligatoire (DECISIONS §4.1) : la clé est générée **par tentative de clôture**
 * et conservée par l'appelant en cas de nouvelle tentative — ici une clôture n'est tentée qu'une
 * fois par vague (le moteur ne réémet pas `vague-terminee`).
 */
export async function cloturerVagueRun(
  runId: string,
  index: number,
  faits: WaveResultInput,
): Promise<WaveResultResponse> {
  return requete({
    methode: 'POST',
    chemin: API_PATHS.runs.waveResult(runId, index),
    schema: waveResultResponseSchema,
    corps: faits,
    cleIdempotence: genererCleIdempotence(),
  });
}

/**
 * `POST /runs/:runId/towers/:towerId/upgrade` — passage au niveau suivant.
 *
 * Corps **vide** : le coût (`coutParNiveau × niveau cible`) et le niveau atteint sont calculés
 * par le serveur (DECISIONS §1.3 A-6, §8.8).
 */
export async function ameliorerTourRun(
  runId: string,
  towerId: string,
): Promise<TowerUpgradeResponse> {
  return requete({
    methode: 'POST',
    chemin: API_PATHS.runs.towerUpgrade(runId, towerId),
    schema: towerUpgradeResponseSchema,
    corps: {},
    cleIdempotence: genererCleIdempotence(),
  });
}

/**
 * `POST /runs/:runId/cards/options` — les 3 cartes proposées après une vague close.
 *
 * Une option verrouillée arrive sans `descKey`, sans `effects` et sans `tags` : son effet ne
 * transite **pas** avant acquisition (F13). L'appelant ne doit donc rien afficher de plus que
 * `{cardId, nameKey, rarity, effect: null}`.
 */
export async function demanderOptionsCartes(
  runId: string,
  entree: CardOptionsInput,
): Promise<CardOptionsResponse> {
  return requete({
    methode: 'POST',
    chemin: API_PATHS.runs.cardOptions(runId),
    schema: cardOptionsResponseSchema,
    corps: entree,
  });
}

/**
 * `POST /runs/:runId/cards/choose` — choix d'une des trois cartes.
 *
 * Rejouer le **même** choix rend `200` sans second déblocage ; un **autre** `cardId` sur une
 * vague déjà choisie répond `409 WAVE_ALREADY_CLOSED`. Aucune clé d'idempotence n'est nécessaire :
 * l'idempotence est portée par `(run, vague)` côté serveur.
 */
export async function choisirCarteRun(
  runId: string,
  entree: CardChooseInput,
): Promise<CardChooseResponse> {
  return requete({
    methode: 'POST',
    chemin: API_PATHS.runs.cardChoose(runId),
    schema: cardChooseResponseSchema,
    corps: entree,
  });
}

/**
 * `POST /runs/:runId/end` — fin de run.
 *
 * `reason` est le seul champ : `victoire` (toutes les vagues closes, gains validés) ou `defaite`
 * (PV à 0, solde de run perdu). Le serveur décide des montants versés ; la réponse est la seule
 * source des éclats affichés à l'écran de fin.
 */
export async function terminerRunRun(runId: string, entree: RunEndInput): Promise<RunEndResponse> {
  return requete({
    methode: 'POST',
    chemin: API_PATHS.runs.end(runId),
    schema: runEndResponseSchema,
    corps: entree,
    cleIdempotence: genererCleIdempotence(),
  });
}
